<template>
  <div>
    <div class="row">
      <div class="col-xl-12">
        <UpperSidebar v-if="pages" :prop_categories="pages" summary-page-name="Consumption" />
      </div>
    </div>
    <div class="row">
      <div v-for="(categoryConsumptionStat, index) in categoryConsumptionStats" v-bind:key="index" class="col-xl-4">
        <stats-card :loading="loadingCategoryConsumptionStats" chart-height="100%">
          <div class="icon-big text-center" :class="`icon-${categoryConsumptionStat.type}`" slot="header">
            <i :class="categoryConsumptionStat.icon"></i>
          </div>
          <div class="numbers" slot="content">
            <p>{{ categoryConsumptionStat.title }}</p>
            {{ categoryConsumptionStat.value }}
          </div>
          <div class="stats" slot="footer">
            <div class="flex justify-content-between">
              <div><i :class="categoryConsumptionStat.footerIcon"></i> {{ categoryConsumptionStat.footerText }}</div>
              <div>
                <div
                  v-if="loadingCategoryConsumptionStats"
                  :class="{ bgBlur: true }"
                  style="height: 20%; width: 100%"
                ></div>
                <div v-if="categoryConsumptionStat.diffPrevMonth > 0">
                  <i class="fas fa-caret-down caretUp text-primary"></i>
                  <span class="text-primary"> {{ categoryConsumptionStat.secondFooterText }}</span>
                </div>
                <div v-else>
                  <i class="fas fa-caret-up caretUp text-danger"></i>
                  <span class="text-danger"> {{ categoryConsumptionStat.secondFooterText }}</span>
                </div>
              </div>
            </div>
          </div>
        </stats-card>
      </div>
    </div>
    <div class="row">
      <div class="col-xl-12">
        <BarChartCard
          :chartHeight="300"
          :title="consumption_category.name + ' Consumption'"
          :chartData="consumptionChart.data"
          :options="consumptionChart.options"
          :loading="loadingConsumptionBarChart"
          :info-text="
            'This card shows your annual ' +
            consumption_category.name +
            ' consumption. Compared to the previous year as well as the average temperature for the particular months.'
          "
        >
          <div v-if="consumption_category_id === 1" slot="dropdown">
            <multiselect
              v-model="consumption_meter"
              :options="consumption_meters"
              :loading="loadingConsumptionMeters"
              :custom-label="addressWithMeterNumber"
              :multiple="false"
              :close-on-select="true"
              :allowEmpty="false"
              @select="consumptionMeterSelected($event)"
              :show-labels="false"
              placeholder="Select meter #"
            >
              <template slot="singleLabel" slot-scope="props">
                <div>
                  <span> <span v-if="props.option.id !== 0"></span> {{ addressWithMeterNumber(props.option) }}</span>
                </div>
              </template>
            </multiselect>
          </div>
        </BarChartCard>
      </div>
    </div>
    <div v-if="showFuelGasAndDieselChart" class="row">
      <div class="col-xl-12">
        <CompareFuelDieselGasConsumptions
          ref="FuelDieselGasComponent"
          :consumption_category_id="consumption_category_id"
          :title="titleCompare"
        ></CompareFuelDieselGasConsumptions>
      </div>
    </div>
    <div class="row">
      <ConsumptionNotifications
        :consumption_category_id="consumption_category_id"
        @meter_selected="consumptionMeterSelected($event)"
      />
    </div>
    <div class="row">
      <div class="col">
        <ConsumptionCategoryTips :consumption_category_id="consumption_category_id"></ConsumptionCategoryTips>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <UploadConsumptionDataCard
          @saved="reloadData()"
          @reload-invoice-count="loadInvoiceCount()"
          class="no-print"
          :consumption_category_id="consumption_category_id"
          :consumption_category="consumptionCategory"
          :invoice_count="invoice_count"
        ></UploadConsumptionDataCard>
      </div>
    </div>
  </div>
</template>

<script>
import UpperSidebar from '@/components/SidebarPlugin/UpperSidebar';
import ConsumptionPagesController from '@/controllers/Consumption/ConsumptionPagesController';
import CategoryConsumptionStatsController from '@/controllers/Consumption/CategoryConsumptionStatsController';
import TotalConsumptionDataController from '@/controllers/Consumption/TotalConsumptionDataController';
import ConsumptionMeterController from '@/controllers/Consumption/ConsumptionMeterController';
import TotalConsumptionDataByMeterController from '@/controllers/Consumption/TotalConsumptionDataByMeterController';
import StatsCard from '@/components/Cards/StatsCard';
import BarChartCard from '@/components/Cards/BarChartCard';
import CompareFuelDieselGasConsumptions from '@/components/consumption/CompareFuelDieselGasConsumptions';
import UploadConsumptionDataCard from '@/components/consumption/UploadConsumptionDataCard';
import ConsumptionCategoryTips from '@/components/consumption/ConsumptionCategoryTips';
import ConsumptionCategoriesController from '@/controllers/Consumption/ConsumptionCategoriesController';
import MoonLoader from '@/components/svg/SvgLoaderMoon';
import ConsumptionNotifications from '@/pages/Consumption/ConsumptionNotifications';

function getShortMonthName(monthNumber) {
  const date = new Date(2024, monthNumber);
  return date.toLocaleString('en-US', { month: 'short' });
}

export default {
  name: 'CategoryConsumptionPage',
  components: {
    ConsumptionNotifications,
    MoonLoader,
    ConsumptionCategoryTips,
    UploadConsumptionDataCard,
    CompareFuelDieselGasConsumptions,
    BarChartCard,
    StatsCard,
    UpperSidebar,
  },
  props: {
    consumption_category_id: Number,
    showFuelGasAndDieselChart: Boolean,
    titleCompare: String,
    consumption_category: Object,
    colors: Object,
  },
  data() {
    return {
      pages: ConsumptionPagesController.pages(),
      consumption_meter: {
        id: 0,
        meter_number: 'All',
      },
      consumptionChart: {
        data: {
          labels: [Array.from({ length: 24 }, (_, i) => getShortMonthName(i % 12))],
          datasets: [
            {
              label: 'Total Usage 2024',
              data: [
                290000, 230000, 260000, 300000, 310000, 320000, 290000, 270000, 280000, 260500, 270000, 250000, 300000,
                310000, 290000, 270000, 280000, 320000, 300000, 310000, 290000, 270500, 280000, 320000,
              ],
              yAxisID: 1,
              backgroundColor: this.colors.primary,
              order: 3,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          animation: false,
          showAllTooltips: false,
          scales: {
            yAxes: [
              {
                id: 1,
                position: 'left',
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
          legend: {
            display: true,
            labels: {
              generateLabels: function (chart) {
                const calendarYearsCount = chart.data.datasets ? Math.ceil(chart.data.datasets[0].data.length / 12) : 2;
                const colors = Object.values(this.colors);

                const currentYear = new Date().getFullYear();
                return Array.from({ length: calendarYearsCount }, (_, i) => ({
                  text: `Total Usage ${currentYear - i} (kWh)`,
                  fillStyle: colors[i],
                })).reverse();
              }.bind(this),
            },
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                const currentYear = new Date().getFullYear();
                const yearOffset = tooltipItem.index > 11 ? 0 : 1;

                const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                return `Total Usage ${currentYear - yearOffset} (kWh): ${value}`;
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: 'Chart.js Doughnut Chart',
            },
          },
        },
      },
      consumptionMeterAllOption: {
        id: 0,
        meter_number: 'All',
      },
      consumption_meters: [],
      totalEnergyConsumption: null,
      loadingConsumptionMeters: false,
      loadingCategoryConsumptionStats: false,
      loadingConsumptionBarChart: false,
      categoryConsumptionStats: [
        {
          title: 'Category CO2 Footprint (lbs)',
          value: '2.45m',
          icon: 'fas fa-smog',
          type: 'danger',
          footerIcon: 'ti-calendar',
          footerText: '2021',
          secondFooterText: '55% more than last year',
          diffPrevMonth: -55.48,
        },
        {
          title: 'Average Month Usage',
          value: '279.74k',
          icon: 'fas fa-chart-pie',
          type: 'success',
          footerIcon: 'ti-calendar',
          footerText: '2021',
          secondFooterText: '7% less than last year',
          diffPrevMonth: 6.86,
        },
        {
          title: 'Max Month Usage',
          value: '315.49k',
          icon: 'fas fa-arrow-up',
          type: 'success',
          footerIcon: 'ti-calendar',
          footerText: '2021',
          secondFooterText: '7% less than last year',
          diffPrevMonth: 6.5,
        },
      ],
      consumptionCategory: null,
      invoice_count: 0,
    };
  },
  created() {
    this.loadConsumptionData();
    if (this.consumption_category_id === 1) this.loadConsumptionMeters();
    this.setConsumptionCategory();
    this.loadCategoryConsumptionStats();
    this.loadInvoiceCount();
  },
  methods: {
    addressWithMeterNumber({ address, meter_number }) {
      if (address) return `${address} — [ # ${meter_number} ]`;
      else return `${meter_number}`;
    },
    async loadConsumptionData() {
      this.loadingConsumptionBarChart = true;
      let totalConsumption = await this.getConsumptionData();
      await this.setConsumptionData(totalConsumption);
      this.loadingConsumptionBarChart = false;
      return totalConsumption;
    },
    async getConsumptionData() {
      return await TotalConsumptionDataController.show(this.consumption_category_id);
    },
    async loadCategoryConsumptionStats() {
      this.loadingCategoryConsumptionStats = true;
      this.categoryConsumptionStats = await CategoryConsumptionStatsController.index(this.consumption_category_id);
      this.loadingCategoryConsumptionStats = false;
    },
    async loadConsumptionMeters() {
      this.loadingConsumptionMeters = true;
      this.consumption_meters = await ConsumptionMeterController.index();
      this.consumption_meters.unshift(this.consumptionMeterAllOption);
      this.loadingConsumptionMeters = false;
    },
    async consumptionMeterSelected(meter) {
      this.loadingConsumptionBarChart = true;
      this.consumption_meter = meter;
      if (parseInt(meter.id) === 0) {
        let totalEnergyConsumption = await this.getConsumptionData();
        this.setConsumptionData(totalEnergyConsumption);
      } else {
        let totalEnergyConsumptionForMeter = await this.getTotalEnergyConsumptionDataForMeter(meter.id);
        this.setConsumptionData(totalEnergyConsumptionForMeter);
      }
      this.loadingConsumptionBarChart = false;
    },
    async getTotalEnergyConsumptionDataForMeter(meter_id) {
      return await TotalConsumptionDataByMeterController.show(meter_id);
    },
    setConsumptionData(totalConsumption) {
      this.totalConsumption = totalConsumption;
      this.updateConsumptionBarChart();
    },
    getCurrentYear() {
      return new Date().getFullYear();
    },
    updateConsumptionBarChart() {
      const calendarYearsCount = Math.min(this.totalConsumption.series.length / 2, 2);
      let consumptionData = [];
      let bgColors = [];
      const colors1 = Object.values(this.colors);
      for (let i = 0; i < calendarYearsCount; i++) {
        consumptionData.unshift(...this.totalConsumption.series[i]);

        const colors = new Array(this.totalConsumption.series[i].length).fill(colors1[i]);
        bgColors.unshift(...colors);
      }
      this.consumptionChart = Object.assign({}, this.consumptionChart, {
        data: {
          labels: [...this.totalConsumption.labels, ...this.totalConsumption.labels],
          datasets: [
            {
              label: '2024', // First dataset for 2024
              data: consumptionData,
              backgroundColor: [...bgColors],
              // borderColor: '#1E88E5',
              borderWidth: 1,
            },
          ],
        },
      });
    },
    reloadData() {
      this.loadConsumptionData();
      if (this.consumption_category_id === 1) this.loadConsumptionMeters();
      this.$refs.FuelDieselGasComponent.loadGasFuelDieselConsumptionData(
        this.$refs.FuelDieselGasComponent.getCurrentYear()
      );
      this.loadCategoryConsumptionStats();
    },
    async setConsumptionCategory() {
      this.consumptionCategory = await ConsumptionCategoriesController.show(this.consumption_category_id);
    },
    isWaterConsumption(consumption_category_id) {
      return consumption_category_id === 5;
    },
    async loadInvoiceCount() {
      this.invoice_count = await CategoryConsumptionStatsController.count(this.consumption_category_id);
    },
  },
};
</script>

<style scoped></style>
